/**
 * #2226 返金の引換券 (お客様側)。決まりは services/pay/api/domains/shop/usecase/payment/refund_voucher.py (V1〜V7)。
 *
 * カードの解約・期限切れなどで返金をカードに戻せなかったとき、返し方は 2 つ (#2308 ②):
 * - **対面**: お店でこの画面の QR とコードを見せ、お店が読み取ってから現金で返す
 * - **オンライン**: お店に行けないとき、ご自身の PayPay / PayPal の請求リンクをここから送り、お店がそれに払う
 *   (決まりは services/pay/api/domains/shop/usecase/payment/refund_online.py R1〜R8。本人がログインして送ったリンクだけ)
 * コードは本人にしか見えない (お店の一覧にもメールにも出さない)。
 * 引換券は日次のジョブが作るので、失敗した当日はまだ無いことがある (404 → 明朝までに用意)。
 */
import { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { useI18n } from '../i18n/useI18n'
import { useStore } from '../store/useStore'
import { api, ApiError, type RefundBankAccount, type RefundOnlineKind, type RefundVoucher } from '../services/api'

const KIND_LABEL: Record<RefundOnlineKind, string> = { paypay: 'PayPay', paypal: 'PayPal', bank_transfer: '' }
const EMPTY_BANK: RefundBankAccount = { bankName: '', branchName: '', accountType: 'ordinary', accountNumber: '', holderName: '' }

const fmtDate = (iso: string | null | undefined, locale: string): string => {
  if (!iso) return '-'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' })
}

/** 予約 (#2226)・体験のチケット (#2298)・会計 (#2308、取引の履歴からの返金) のどれか 1 つを渡す */
export default function RefundVoucherCard({ reservationId, ticketId, transactionId }: {
  reservationId?: string; ticketId?: string; transactionId?: string
}) {
  const { t, locale } = useI18n()
  const { user } = useStore()
  const [voucher, setVoucher] = useState<RefundVoucher | null>(null)
  const [state, setState] = useState<'idle' | 'loading' | 'notYet' | 'failed'>('idle')
  // オンラインの請求リンク (入力中)
  const [linkOpen, setLinkOpen] = useState(false)
  const [kind, setKind] = useState<RefundOnlineKind | null>(null)
  const [url, setUrl] = useState('')
  const [bank, setBank] = useState<RefundBankAccount>(EMPTY_BANK)
  const [busy, setBusy] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)

  const open = async () => {
    setState('loading')
    try {
      setVoucher(ticketId ? await api.getMyTicketRefundVoucher(ticketId)
        : transactionId ? await api.getMySaleRefundVoucher(transactionId)
          : await api.getMyRefundVoucher(reservationId ?? ''))
      setState('idle')
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setState('notYet')
      } else {
        console.error('[RefundVoucherCard] load failed:', e)
        setState('failed')
      }
    }
  }

  // #2308 ③ R10 そのお店が使える方法だけ。既定は、日本の方は PayPay (使えなければ銀行振込)、海外の方は PayPal
  const kinds: RefundOnlineKind[] = voucher?.online?.availableKinds?.length ? voucher.online.availableKinds : ['bank_transfer']
  const defaultKind: RefundOnlineKind = user?.nationality === 'JP'
    ? (kinds.includes('paypay') ? 'paypay' : 'bank_transfer')
    : (kinds.includes('paypal') ? 'paypal' : kinds.includes('paypay') ? 'paypay' : 'bank_transfer')
  const activeKind: RefundOnlineKind = kind && kinds.includes(kind) ? kind : defaultKind

  const sendLink = async () => {
    if (!voucher || busy) return
    setBusy(true)
    setLinkError(null)
    try {
      setVoucher(activeKind === 'bank_transfer'
        ? await api.submitRefundBankAccount(voucher.id, { ...bank, accountNumber: bank.accountNumber.trim() })
        : await api.submitRefundOnlineLink(voucher.id, url.trim()))
      setLinkOpen(false)
      setUrl('')
      setBank(EMPTY_BANK)
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) {
        setLinkError(t(activeKind === 'bank_transfer' ? 'refundOnline.invalidAccount' : 'refundOnline.invalid'))
      } else if (e instanceof ApiError && e.status === 409 && e.code === 'kind_not_available') {
        setLinkError(t('refundOnline.kindNotAvailable'))
      } else {
        console.error('[RefundVoucherCard] link failed:', e)
        setLinkError(t('refundOnline.sendFailed'))
      }
    } finally {
      setBusy(false)
    }
  }

  const notReceived = async () => {
    if (!voucher || busy) return
    const k = KIND_LABEL[voucher.online?.kind ?? 'paypay']
    if (!window.confirm(t('refundOnline.notReceivedConfirm', { kind: k }))) return
    setBusy(true)
    try {
      setVoucher(await api.claimRefundNotReceived(voucher.id))
    } catch (e) {
      console.error('[RefundVoucherCard] claim failed:', e)
      setLinkError(t('refundOnline.sendFailed'))
    } finally {
      setBusy(false)
    }
  }

  if (!voucher) {
    return (
      <div className="mt-2" data-testid="refund-voucher-open">
        <button type="button" onClick={() => void open()} disabled={state === 'loading'}
          className="rounded-lg border border-navy px-3 py-1.5 font-body text-xs font-semibold text-navy tap-effect disabled:opacity-50">
          {t('myReservations.refundVoucherShow')}
        </button>
        {state === 'notYet' && (
          <p className="mt-1 font-body text-[11px] text-text-secondary">{t('myReservations.refundVoucherNotYet')}</p>
        )}
        {state === 'failed' && (
          <p className="mt-1 font-body text-[11px] text-yokoso-red">{t('myReservations.refundVoucherLoadFailed')}</p>
        )}
      </div>
    )
  }

  const online = voucher.online
  const labelOf = (k: RefundOnlineKind | null | undefined) => (k === 'bank_transfer' ? t('refundOnline.kindBank') : KIND_LABEL[k ?? 'paypay'])
  const kindLabel = labelOf(online?.kind ?? activeKind)
  // #2478 銀行口座は「リンク」ではない。送る・差し替えるの文言を分ける
  const sendLabel = t(activeKind === 'bank_transfer' ? 'refundOnline.sendBank' : 'refundOnline.send')
  const replaceLabel = t(online?.kind === 'bank_transfer' ? 'refundOnline.replaceBank' : 'refundOnline.replace')
  const amount = voucher.amount.toLocaleString()
  const bankLine = (b: RefundBankAccount | null | undefined) => b
    ? t('refundOnline.bankLine', { bank: b.bankName, branch: b.branchName,
      type: t(b.accountType === 'current' ? 'refundOnline.accountCurrent' : 'refundOnline.accountOrdinary'),
      number: b.accountNumber, holder: b.holderName })
    : ''
  const canSend = activeKind === 'bank_transfer'
    ? Object.values({ ...bank, accountNumber: bank.accountNumber.trim() }).every((v) => v.length > 0)
    : url.trim().length >= 12

  // オンラインで返し終えた (お店が払い、14 日の申し出が無かった)
  if (voucher.status === 'redeemed' && (voucher.method === 'paypay' || voucher.method === 'paypal' || voucher.method === 'bank_transfer')) {
    return (
      <p className="mt-2 font-body text-xs text-matcha" data-testid="refund-online-done">
        {t('refundOnline.doneOnline', { kind: labelOf(voucher.method) })}
      </p>
    )
  }

  // #2308 ② お店がオンラインで払った (14 日の窓) / お客様が「届いていない」と申し出た
  if (voucher.status === 'paid' && online) {
    return (
      <div className="mt-2 rounded-xl border border-gray-200 bg-white p-3" data-testid="refund-online-paid">
        <p className="font-body text-xs text-text-primary">
          {t('refundOnline.paid', { kind: kindLabel, amount, ref: online.paid?.ref ?? '-' })}
        </p>
        {online.state === 'claimed' ? (
          <p className="mt-1 font-body text-[11px] text-text-secondary" data-testid="refund-online-claimed">{t('refundOnline.claimed')}</p>
        ) : (
          <>
            <p className="mt-1 font-body text-[11px] text-text-secondary">
              {t('refundOnline.notReceivedHint', { until: fmtDate(online.claimUntil, locale) })}
            </p>
            <button type="button" onClick={() => void notReceived()} disabled={busy}
              className="mt-2 rounded-lg border border-yokoso-red px-3 py-1.5 font-body text-xs font-semibold text-yokoso-red tap-effect disabled:opacity-50">
              {t('refundOnline.notReceived')}
            </button>
            {linkError && <p className="mt-1 font-body text-[11px] text-yokoso-red">{linkError}</p>}
          </>
        )}
      </div>
    )
  }

  // この画面はカードに返せない未返金があるときだけ出る。今の引換券が使用済み・無効なら、
  // 新しい額の引換券を日次のジョブが用意する途中 (前の引換券は引き換え済み・出し直し)
  if (voucher.status !== 'issued') {
    return (
      <p className="mt-2 font-body text-xs text-text-secondary" data-testid="refund-voucher-pending">
        {t('myReservations.refundVoucherNotYet')}
      </p>
    )
  }

  const requested = online?.state === 'requested'
  const expired = online?.state === 'expired'

  return (
    <div className="mt-2 rounded-xl border border-gray-200 bg-white p-3 text-center" data-testid="refund-voucher">
      <p className="font-body text-xs font-semibold text-text-primary">{t('myReservations.refundVoucherTitle')}</p>
      <div className="mx-auto mt-2 w-fit rounded-lg bg-white p-2">
        <QRCodeSVG value={voucher.qr} size={170} level="M" />
      </div>
      <p className="mt-2 font-mono text-base tracking-widest text-text-primary">{voucher.code}</p>
      <p className="font-display text-lg font-bold text-text-primary">¥{amount}</p>
      <p className="mt-1 font-body text-[11px] leading-relaxed text-text-secondary">{t('myReservations.refundVoucherHint')}</p>

      {/* #2308 ② オンライン (お店に行けないとき) */}
      <div className="mt-3 border-t border-gray-100 pt-3 text-left" data-testid="refund-online">
        {requested && online && (
          <div data-testid="refund-online-requested">
            <p className="font-body text-xs text-text-primary">
              {t(online.kind === 'bank_transfer' ? 'refundOnline.requestedBank' : 'refundOnline.requested',
                { kind: kindLabel, deadline: fmtDate(online.deadlineAt, locale) })}
            </p>
            {online.url && <p className="mt-1 break-all font-mono text-[11px] text-text-tertiary">{online.url}</p>}
            {online.bank && <p className="mt-1 font-body text-[11px] text-text-tertiary">{bankLine(online.bank)}</p>}
            {online.kind === 'paypay' && (
              <p className="mt-1 font-body text-[11px] text-text-secondary">{t('refundOnline.expiresNote')}</p>
            )}
          </div>
        )}
        {/* #2308 ③ R11 期日までにお店が払わなかった: カード会社への申し立て (チャージバック) もできる */}
        {online?.disputeAdvised && (
          <p className="mt-2 rounded-lg border border-yokoso-red/40 bg-yokoso-red/5 p-2 font-body text-[11px] leading-relaxed text-text-primary" data-testid="refund-online-dispute-advice">
            {t('refundOnline.disputeAdvice')}
          </p>
        )}
        {expired && (
          <div data-testid="refund-online-expired">
            <p className="font-body text-xs text-yokoso-red">{t('refundOnline.expired')}</p>
            {online?.contactOps && (
              <p className="mt-1 font-body text-[11px] font-semibold text-yokoso-red">{t('refundOnline.contactOps')}</p>
            )}
          </div>
        )}
        {!requested && !expired && !linkOpen && (
          <p className="font-body text-xs font-semibold text-text-primary">{t('refundOnline.cannotVisit')}</p>
        )}
        {!linkOpen && user?.nationality && user.nationality !== 'JP' && !kinds.includes('paypal') && (
          <p className="mt-1 font-body text-[11px] text-text-secondary" data-testid="refund-online-no-paypal">{t('refundOnline.noPaypalHint')}</p>
        )}

        {!linkOpen ? (
          <button type="button" onClick={() => setLinkOpen(true)}
            className="mt-2 rounded-lg border border-navy px-3 py-1.5 font-body text-xs font-semibold text-navy tap-effect"
            data-testid="refund-online-open">
            {requested ? replaceLabel : sendLabel}
          </button>
        ) : (
          <div className="mt-2 space-y-2" data-testid="refund-online-form">
            <div className="flex gap-1.5">
              {kinds.map((k) => (
                <button key={k} type="button" onClick={() => setKind(k)}
                  className={`flex-1 rounded-lg border px-2 py-1.5 font-body text-xs font-semibold ${activeKind === k
                    ? 'border-navy bg-navy text-white' : 'border-gray-200 bg-white text-text-secondary'}`}
                  data-testid={`refund-online-kind-${k}`}>
                  {labelOf(k)}
                </button>
              ))}
            </div>
            <p className="font-body text-[11px] leading-relaxed text-text-secondary">
              {t(activeKind === 'bank_transfer' ? 'refundOnline.introBank' : 'refundOnline.intro', { kind: labelOf(activeKind) })}
            </p>
            {activeKind !== 'bank_transfer' && (
              <p className="whitespace-pre-line rounded-lg bg-gray-50 p-2 font-body text-[11px] leading-relaxed text-text-secondary">
                {t(activeKind === 'paypay' ? 'refundOnline.stepsPaypay' : 'refundOnline.stepsPaypal', { amount })}
              </p>
            )}
            {activeKind === 'bank_transfer' ? (
              <div className="space-y-1.5" data-testid="refund-online-bank-form">
                {([['bankName', 'refundOnline.bankName'], ['branchName', 'refundOnline.branchName']] as const).map(([key, label]) => (
                  <label key={key} className="block font-body text-[11px] font-medium text-text-secondary">
                    {t(label)}
                    <input value={bank[key]} maxLength={40} onChange={(e) => setBank((b) => ({ ...b, [key]: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 font-body text-sm" data-testid={`refund-online-${key}`} />
                  </label>
                ))}
                <div className="flex gap-1.5">
                  {(['ordinary', 'current'] as const).map((tp) => (
                    <button key={tp} type="button" onClick={() => setBank((b) => ({ ...b, accountType: tp }))}
                      className={`flex-1 rounded-lg border px-2 py-1.5 font-body text-xs ${bank.accountType === tp
                        ? 'border-navy bg-navy text-white' : 'border-gray-200 bg-white text-text-secondary'}`}>
                      {t(tp === 'current' ? 'refundOnline.accountCurrent' : 'refundOnline.accountOrdinary')}
                    </button>
                  ))}
                </div>
                <label className="block font-body text-[11px] font-medium text-text-secondary">
                  {t('refundOnline.accountNumber')}
                  <input value={bank.accountNumber} inputMode="numeric" maxLength={12}
                    onChange={(e) => setBank((b) => ({ ...b, accountNumber: e.target.value }))}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-sm" data-testid="refund-online-accountNumber" />
                </label>
                <label className="block font-body text-[11px] font-medium text-text-secondary">
                  {t('refundOnline.holderName')}
                  <input value={bank.holderName} maxLength={60} onChange={(e) => setBank((b) => ({ ...b, holderName: e.target.value }))}
                    placeholder="ヤマダ タロウ"
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 font-body text-sm" data-testid="refund-online-holderName" />
                </label>
              </div>
            ) : (
              <label className="block font-body text-[11px] font-medium text-text-secondary">
                {t('refundOnline.linkLabel')}
                <input
                  type="url"
                  inputMode="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={activeKind === 'paypay' ? 'https://pay.paypay.ne.jp/…' : 'https://paypal.me/…'}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 font-body text-sm"
                  data-testid="refund-online-url"
                />
              </label>
            )}
            {linkError && <p className="font-body text-[11px] text-yokoso-red">{linkError}</p>}
            <div className="flex gap-2">
              <button type="button" onClick={() => void sendLink()} disabled={busy || !canSend}
                className="flex-1 rounded-full bg-navy py-2 font-body text-xs font-bold text-white tap-effect disabled:opacity-50"
                data-testid="refund-online-send">
                {sendLabel}
              </button>
              <button type="button" onClick={() => { setLinkOpen(false); setLinkError(null) }} disabled={busy}
                className="rounded-full border border-gray-200 px-4 py-2 font-body text-xs text-text-secondary">
                {t('common.close')}
              </button>
            </div>
            <p className="font-body text-[10px] text-text-tertiary">{t('refundOnline.security')}</p>
          </div>
        )}
      </div>
    </div>
  )
}
